import { computed, type ComputedRef, unref } from "vue";

import { __bucket } from "~/stores/bucket.store";
import { __identifier } from "~/stores/identifier.store";
import { __subscription } from "~/stores/subscription.store";

import type { Quota } from "~/types/drive";
import { Sub } from "~/types/subscriptions";

import type { SubObjectStorage } from "~/classes/subscriptions/SubscriptionObjectStorage";
import { getLabel, getType } from "~/composables/badges";
import { useStorageStats } from "~/composables/subscriptions/useStorageStats";
import { convertToToBytes, formatIntegerUnits, getSizeObject, toBytes } from "~/composables/units";

const MILLISECONDS_PER_SECOND = 1000;

export function useObjectStorageStats (objectStorage: SubObjectStorage): {
	rawObjectsAmount: ComputedRef<number>;
	objectsAmount: ComputedRef<Quota>;
	subscribedSpace: number;
	allocatedSpace: ComputedRef<number>;
	usedSpace: ComputedRef<number>;
	spaceTotal: ComputedRef<Quota | undefined>;
	spaceDisposed: ComputedRef<Quota>;
	spacePercent: ComputedRef<number | undefined>;
	isDisabled: ComputedRef<boolean>;
	isPastDue: ComputedRef<boolean>;
	badgeLabel: ComputedRef<string | undefined>;
	badgeType: ComputedRef<string | undefined>;
	amountBuckets: ComputedRef<number>;
	amountIdentifiers: ComputedRef<number>;
	isLoading: ComputedRef<boolean>;
	expiredAt: ComputedRef<number>;
	startAt: ComputedRef<number>;
} {
	const rawObjectsAmount = computed<number>(() => {
		try {
			return unref(objectStorage.objectsAmount) || 0;
		} catch (error) {
			console.error("Error accessing objectsAmount:", error);
			return 0;
		}
	});

	const objectsAmount = computed<Quota>(() => {
		try {
			return getValueUnits(unref(objectStorage.objectsAmount) || 0);
		} catch (error) {
			console.error("Error computing objectsAmount:", error);
			return { quota: 0, unit: "" };
		}
	});

	const spaceTotal = computed<Quota | undefined>(() => {
		try {
			if (unref(objectStorage.isUnlimited) || !objectStorage.spaceTotal) return;
			return getSizeObject(convertToToBytes(unref(objectStorage.spaceTotal)));
		} catch (error) {
			console.error("Error computing spaceTotal:", error);
			return undefined;
		}
	});

	const spaceDisposed = computed<Quota>(() => {
		try {
			return getSizeObject(unref(objectStorage.spaceDisposed) || 0);
		} catch (error) {
			console.error("Error computing spaceDisposed:", error);
			return { quota: 0, unit: "o" };
		}
	});

	const spacePercent = computed<number | undefined>(() => {
		try {
			if (!spaceTotal.value) return undefined;

			const total = toBytes(spaceTotal.value.quota, spaceTotal.value.unit);
			const used = toBytes(spaceDisposed.value.quota, spaceDisposed.value.unit);

			return total && used ? (used / total) * 100 : undefined;
		} catch (error) {
			console.error("Error computing spacePercent:", error);
			return undefined;
		}
	});

	const isDisabled = computed(() => {
		try {
			return unref(__subscription().getIsDisabled(Sub.Type.ObjectStorage));
		} catch (error) {
			console.error("Error checking isDisabled:", error);
			return false;
		}
	});

	const isPastDue = computed(() => {
		try {
			return objectStorage.status === Sub.Status.PastDue;
		} catch (error) {
			console.error("Error checking isPastDue:", error);
			return false;
		}
	});

	const badgeLabel = computed(() => {
		try {
			return getLabel(isDisabled.value, isPastDue.value);
		} catch (error) {
			console.error("Error computing badgeLabel:", error);
			return undefined;
		}
	});

	const badgeType = computed(() => {
		try {
			return getType(isDisabled.value, isPastDue.value);
		} catch (error) {
			console.error("Error computing badgeType:", error);
			return undefined;
		}
	});

	const amountBuckets = computed(() => {
		try {
			return __bucket().getBucketsLength;
		} catch (error) {
			console.error("Error accessing amountBuckets:", error);
			return 0;
		}
	});

	const amountIdentifiers = computed(() => {
		try {
			return __identifier().getIdentifiersAmount;
		} catch (error) {
			console.error("Error accessing amountIdentifiers:", error);
			return 0;
		}
	});

	const isLoading = computed(() => {
		try {
			return __subscription().getIsLoading(Sub.Type.ObjectStorage);
		} catch (error) {
			console.error("Error checking isLoading:", error);
			return false;
		}
	});

	const expiredAt = computed(() => {
		try {
			return objectStorage.cancelAt
				? objectStorage.cancelAt.getTime()
				: (
					objectStorage.subscriptionItem?.items?.data?.[0]?.current_period_end
						? objectStorage.subscriptionItem.items.data[0].current_period_end * MILLISECONDS_PER_SECOND
						: 0
				);
		} catch (error) {
			console.error("Error computing expiredAt:", error);
			return 0;
		}
	});

	const startAt = computed(() => {
		try {
			return objectStorage.subscriptionItem?.items?.data?.[0]?.current_period_end
				? objectStorage.subscriptionItem.start_date * MILLISECONDS_PER_SECOND
				: 0;
		} catch (error) {
			console.error("Error computing startAt:", error);
			return 0;
		}
	});

	function getValueUnits (baseValue: string | number): Quota {
		const newValue = formatIntegerUnits(parseFloat(`${baseValue}`));
		const [ quota, unit ] = newValue.split(" ");
		return { quota: parseFloat(quota), unit };
	}

	const { subscribedSpace, allocatedSpace, usedSpace } = useStorageStats(unref(objectStorage.spaceTotal) || 0);

	return {
		rawObjectsAmount,
		objectsAmount,
		spaceTotal,
		spaceDisposed,
		spacePercent,
		isDisabled,
		isPastDue,
		badgeLabel,
		badgeType,
		amountBuckets,
		amountIdentifiers,
		isLoading,
		expiredAt,
		startAt,
		subscribedSpace,
		allocatedSpace,
		usedSpace
	};
}
